'use client'

import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useSWR from 'swr'
import {
  createContext,
  useContext,
  useContextSelector,
} from 'use-context-selector'
import type { FC, ReactNode } from 'react'
import Loading from '@/app/components/base/loading'
import {
  fetchCurrentWorkspace,
  fetchLanggeniusVersion,
  fetchUserProfile,
} from '@/service/common'
import type {
  ICurrentWorkspace,
  LangGeniusVersionResponse,
  UserProfileResponse,
} from '@/models/common'
import MaintenanceNotice from '@/app/components/header/maintenance-notice'

export type AppContextValue = {
  userProfile: UserProfileResponse
  mutateUserProfile: VoidFunction
  currentWorkspace: ICurrentWorkspace
  isCurrentWorkspaceManager: boolean
  isCurrentWorkspaceOwner: boolean
  isCurrentWorkspaceEditor: boolean
  isSuper: boolean
  scope: 'system' | 'domain' | 'project'
  mutateCurrentWorkspace: VoidFunction
  pageContainerRef: React.RefObject<HTMLDivElement>
  langeniusVersionInfo: LangGeniusVersionResponse
  useSelector: typeof useSelector
  isWorkspaceResource: (resource: any) => boolean
  isResourceEditor: (resource: any) => boolean
  isResourceOwner: (resource: any) => boolean
  isPublicResource: (resource: any) => boolean
}

const initialLangeniusVersionInfo = {
  current_env: '',
  current_version: '',
  latest_version: '',
  release_date: '',
  release_notes: '',
  version: '',
  can_auto_update: false,
}

const initialWorkspaceInfo: ICurrentWorkspace = {
  id: '',
  name: '',
  plan: '',
  status: '',
  created_at: 0,
  role: 'normal',
  providers: [],
  in_trail: true,
  is_admin_or_owner: false,
  is_editor: false,
}

const AppContext = createContext<AppContextValue>({
  userProfile: {
    id: '',
    name: '',
    email: '',
    avatar: '',
    is_password_set: false,
    is_super: false,
    is_admin: false,
  },
  currentWorkspace: initialWorkspaceInfo,
  isCurrentWorkspaceManager: false,
  isCurrentWorkspaceOwner: false,
  isCurrentWorkspaceEditor: false,
  isSuper: false,
  scope: 'project',
  mutateUserProfile: () => { },
  mutateCurrentWorkspace: () => { },
  pageContainerRef: createRef(),
  langeniusVersionInfo: initialLangeniusVersionInfo,
  useSelector,
  isWorkspaceResource : (resource: any) => false,
  isResourceEditor: (resource: any) => false,
  isResourceOwner: (resource: any) => false,
  isPublicResource: (resource: any) => false,
})

export function useSelector<T>(selector: (value: AppContextValue) => T): T {
  return useContextSelector(AppContext, selector)
}

export type AppContextProviderProps = {
  children: ReactNode
}

export const AppContextProvider: FC<AppContextProviderProps> = ({
  children,
}) => {
  const pageContainerRef = useRef<HTMLDivElement>(null)

  const { data: userProfileResponse, mutate: mutateUserProfile } = useSWR({ url: '/account/profile', params: {} }, fetchUserProfile)
  const { data: currentWorkspaceResponse, mutate: mutateCurrentWorkspace } = useSWR({ url: '/workspaces/current', params: {} }, fetchCurrentWorkspace)

  const [userProfile, setUserProfile] = useState<UserProfileResponse>()
  const [langeniusVersionInfo, setLangeniusVersionInfo] = useState<LangGeniusVersionResponse>(initialLangeniusVersionInfo)
  const [currentWorkspace, setCurrentWorkspace] = useState<ICurrentWorkspace>(initialWorkspaceInfo)
  const isCurrentWorkspaceManager = useMemo(() => currentWorkspace.is_admin_or_owner , [currentWorkspace])
  const isCurrentWorkspaceOwner = useMemo(() => currentWorkspace.is_admin_or_owner , [currentWorkspace])
  const isCurrentWorkspaceEditor = useMemo(() => currentWorkspace.is_editor, [currentWorkspace])
  const isSuper = userProfile?.is_super || false
  const scope = useMemo(() => {
    if (userProfile?.is_super) return 'system'
    if (currentWorkspace.is_admin_or_owner) return 'domain'
    return 'project'
  }, [userProfile, currentWorkspace])

  const isWorkspaceResource = useCallback((resource: any) => {
    if (!resource || !resource.tenant_id) return false
    return resource.tenant_id === currentWorkspace.id
  }, [currentWorkspace])

  const isPublicResource = useCallback((resource: any) => {
    if (!resource || !resource.tenant_id) return false
    return resource.permission === 'all_users'
  }, [])

  const isResourceEditor = useCallback((resource: any) => {
    return isWorkspaceResource(resource) ? isCurrentWorkspaceEditor : isSuper
  }, [isCurrentWorkspaceEditor, isCurrentWorkspaceOwner])

  const isResourceOwner = useCallback((resource: any) => {
    return isWorkspaceResource(resource) ? isCurrentWorkspaceOwner : isSuper
  }, [isCurrentWorkspaceOwner, isSuper])

  const updateUserProfileAndVersion = useCallback(async () => {
    if (userProfileResponse && !userProfileResponse.bodyUsed) {
      const result = await userProfileResponse.json()
      setUserProfile(result)
      const current_version = userProfileResponse.headers.get('x-version')
      const current_env =
        process.env.NODE_ENV === 'development'
          ? 'DEVELOPMENT'
          : userProfileResponse.headers.get('x-env')
      const versionData = await fetchLanggeniusVersion({
        url: '/version',
        params: { current_version },
      })
      setLangeniusVersionInfo({
        ...versionData,
        current_version,
        latest_version: versionData.version,
        current_env,
      })
    }
  }, [userProfileResponse])

  useEffect(() => {
    updateUserProfileAndVersion()
  }, [updateUserProfileAndVersion, userProfileResponse])

  useEffect(() => {
    if (currentWorkspaceResponse) setCurrentWorkspace(currentWorkspaceResponse)
  }, [currentWorkspaceResponse])

  if (!userProfile) return <Loading type='app' />

  return (
    <AppContext.Provider value={{
      userProfile,
      mutateUserProfile,
      pageContainerRef,
      langeniusVersionInfo,
      useSelector,
      currentWorkspace,
      isCurrentWorkspaceManager,
      isCurrentWorkspaceOwner,
      isCurrentWorkspaceEditor,
      isSuper,
      scope,
      mutateCurrentWorkspace,
      isWorkspaceResource,
      isResourceEditor,
      isResourceOwner,
      isPublicResource,
    }}>
      <div className='flex flex-col h-full overflow-y-auto'>
        {globalThis.document?.body?.getAttribute('data-public-maintenance-notice') && <MaintenanceNotice />}
        <div ref={pageContainerRef} className='grow relative flex flex-col overflow-y-auto overflow-x-hidden bg-gray-100 h-[100vh]'>
          {children}
        </div>
      </div>
    </AppContext.Provider>
  )
}

export const useAppContext = () => useContext(AppContext)

export default AppContext
